import React, { useState, useCallback } from 'react';
import Slider from '@/components/ui/general/Slider/Slider';
import ToggleButtons from '@/components/ui/general/ToggleButtons';
import { generateGameName } from '@brandscripts/generateGameName';
import { Copy, Check } from 'lucide-react';
import { useT } from '@/i18n';

function CreateGameForm({ selectedPlayer, onCreateGame, error, isLoading }) {
  const t = useT();

  //Genera el codigo de un juego nuevo
  const generateGameCode = () => {
    return Math.random().toString(36).substr(2, 6).toUpperCase();
  };

  const [gameName, setGameName] = useState(generateGameName());
  const [mapWidth, setMapWidth] = useState(20);
  const [mapHeight, setMapHeight] = useState(20);
  const [isPublic, setIsPublic] = useState(true);
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);  

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedPlayer) {
      return;
    }
    onCreateGame({
      player_id: selectedPlayer,
      name: gameName || null,
      map_width: parseInt(mapWidth, 10),
      map_height: parseInt(mapHeight, 10),
      max_players: 2,
      visibility: isPublic ? 'public' : 'private',
      access_code: isPublic ? null : password
    });

  };

  const handleCopyCode = async () => {
    if (!password) return;

    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="create-game-form">
      <h4>{t('create_game.title')}</h4>
      {error && <p className="error-message">{error}</p>}

      {/* Section 1: Game Settings */}
      <div className="form-section">
        <div className="form-group">
          <label htmlFor="gameName">
            {t('create_game.game_name')}
          </label>
          <input
            id="gameName"
            type="text"
            value={gameName}
            onChange={(e) => setGameName(e.target.value)}
            placeholder={t('create_game.game_name_placeholder')}
          />
        </div>
      </div>

      {/* Section 2: Map Dimensions */}
      <div className="form-section">
        <div className="map-dimensions-row">
          <Slider
            id="mapWidth"
            label={t('create_game.map_width')}
            value={mapWidth}
            onChange={setMapWidth}
            min={10}
            max={50}
          />

          <Slider
            id="mapHeight"
            label={t('create_game.map_height')}
            value={mapHeight}
            onChange={setMapHeight}
            min={10}
            max={50}
          />
        </div>
      </div>

      {/* Section 3: Access Settings */}
      <div className="form-section">
        <div className="visibility-row">
          <ToggleButtons
            options={[
              { value: 'private', label: t('create_game.private') },
              { value: 'public', label: t('create_game.public') },
            ]}
            value={isPublic ? 'public' : 'private'}
            onChange={(val) => {
              const next = val === 'public';
              setIsPublic(next);
              if (!next) {
                setPassword(generateGameCode());
              } else {
                setPassword('');
              }
            }}
          />

          <div className="form-group code-input-wrapper">
            <label htmlFor="password">{t('create_game.access_code')}</label>
            <input
              id="password"
              type="text"
              value={password}
              disabled={isPublic}
              readOnly
            />
            <button
              type="button"
              className="copy-button-inside"
              onClick={handleCopyCode}
              disabled={isPublic || !password}
              title={copied ? t('create_game.copied') : t('create_game.copy_code')}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
        </div>
      </div>

      <button type="submit" disabled={!selectedPlayer || isLoading} className="button button-cta button-cta-combo">
        {isLoading ? t('create_game.creating') : t('create_game.create')}
      </button>

      {!selectedPlayer && <p className="warning-message">{t('create_game.select_player')}</p>}
    </form>
  );
}

export default CreateGameForm;